import React, { useState } from 'react';
import {
Box,
Container,
Typography,
Paper,
Accordion,
AccordionSummary,
AccordionDetails,
Grid,
Card,
CardContent,
Button,
Chip,
Divider,
List,
ListItem,
ListItemIcon,
ListItemText,
Stepper,
Step,
StepLabel,
StepContent,
useTheme,
useMediaQuery,
Fab,
alpha,
Slide,
CardMedia,
IconButton
} from '@mui/material';
import {
ExpandMore as ExpandMoreIcon,
Help as HelpIcon,
Book as BookIcon,
School as SchoolIcon,
VideoLibrary as VideoIcon,
Download as DownloadIcon,
Search as SearchIcon,
Article as ArticleIcon,
People as PeopleIcon,
Security as SecurityIcon,
Settings as SettingsIcon,
Dashboard as DashboardIcon,
TrendingUp as TrendingUpIcon,
Lightbulb as LightbulbIcon,
CheckCircle as CheckCircleIcon,
PlayArrow as PlayArrowIcon,
Home as HomeIcon,
Whatshot as WhatshotIcon,
Science as ScienceIcon,
Inventory as InventoryIcon,
MoreHoriz as MoreHorizIcon,
YouTube as YouTubeIcon,
Subscriptions as SubscriptionsIcon,
ThumbUp as ThumbUpIcon
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import NavigationMenu from '../navigation/NavigationMenu';
import { ResearchLinks, PublicationLinks, ProjectsLinks, ProductsLinks, MoreLinks } from '../navigation/Data';

const GeneralResources = () => {
const theme = useTheme();
const isMobile = useMediaQuery(theme.breakpoints.down('md'));
const [expandedFAQ, setExpandedFAQ] = useState(false);
const [searchTerm, setSearchTerm] = useState('');
const [activeCategory, setActiveCategory] = useState('all');

// YouTube Channel Info
const youtubeChannel = {
    name: "RIA Uganda",
    url: "https://www.youtube.com/@riauganda",
    embedUrl: "https://www.youtube.com/embed/videoseries?list=UUxxxxxxxxxxxxx", // Replace with actual playlist/embed URL
    subscribers: "1.2K",
    totalVideos: "45+",
    description: "Educational content on research, innovation, and academic excellence"
};

// Updated FAQ Categories based on platform sections
const faqCategories = [
    { key: 'all', label: 'All Questions', icon: <WhatshotIcon />, count: 0 },
    { key: 'research', label: 'Research', icon: <ScienceIcon />, count: 8 },
    { key: 'projects', label: 'Projects', icon: <TrendingUpIcon />, count: 6 },
    { key: 'products', label: 'Products', icon: <InventoryIcon />, count: 5 },
    { key: 'publications', label: 'Publications', icon: <ArticleIcon />, count: 4 },
    { key: 'account', label: 'Account', icon: <PeopleIcon />, count: 5 }
];

// Updated FAQ Data based on App.js navigation and platform features
const faqData = [
    // Research Related FAQs
    {
        id: 1,
        question: "How do I submit new research on the platform?",
        answer: "To submit new research, navigate to the Research section and click 'Submit' from the navigation menu. You'll need to provide research title, description, methodology, findings, and relevant themes. All submissions undergo review before publication.",
        category: 'research',
        popular: true,
        link: "/research-submit"
    },
    {
        id: 2,
        question: "How can I browse and search for existing research?",
        answer: "Go to the 'Research' section in the main navigation. You can search by keywords, filter by themes, authors, affiliations, or date. Use the advanced search options for more precise results.",
        category: 'research',
        popular: true,
        link: "/researches"
    },
    {
        id: 3,
        question: "What are Research Themes and how do I use them?",
        answer: "Research Themes help categorize research by topic area. You can browse all available themes in the 'Themes' section. When submitting research, you can select multiple relevant themes to increase visibility.",
        category: 'research',
        popular: false,
        link: "/themes"
    },
    {
        id: 4,
        question: "How do I find other researchers in my field?",
        answer: "Visit the 'Authors' section to browse all registered researchers. You can search by name, institution, research interests, or publications. Connect with researchers to collaborate on projects.",
        category: 'research',
        popular: false,
        link: "/authors"
    },
    {
        id: 5,
        question: "What are Affiliations and how do they work?",
        answer: "Affiliations represent academic institutions, research centers, or organizations. You can browse all registered affiliations in the 'Affiliations' section. Researchers can be associated with multiple affiliations.",
        category: 'research',
        popular: false,
        link: "/affiliations"
    },
    {
        id: 6,
        question: "How do I view detailed research information?",
        answer: "Click on any research title from the researches list to view detailed information including abstract, methodology, findings, authors, and citations. Use the 'Research Details' page for comprehensive information.",
        category: 'research',
        popular: false,
        link: "/research-details"
    },
    {
        id: 7,
        question: "Can I edit my submitted research?",
        answer: "Yes, you can edit your own research submissions. Go to your profile, find your research under 'My Research', and click the edit button. Changes will be reviewed before republication.",
        category: 'research',
        popular: false
    },
    {
        id: 8,
        question: "How do I view all research at once?",
        answer: "Use the 'Research View All' option from the research section to see all available research in a paginated list format. You can sort by newest, most viewed, or most cited.",
        category: 'research',
        popular: false,
        link: "/research-views"
    },

    // Projects Related FAQs
    {
        id: 9,
        question: "How do I submit a new research project?",
        answer: "Navigate to the Projects section and click 'Submit'. Provide project title, description, start/end dates, core investigator, team members, objectives, activities, budget, outcomes, evaluation plan, and sustainability plan.",
        category: 'projects',
        popular: true,
        link: "/project-submit"
    },
    {
        id: 10,
        question: "How can I browse existing projects?",
        answer: "Go to the 'Projects' section in the main navigation. You can search by title, researcher, university, or keywords. Filter by status (Active, Upcoming, Completed) to find relevant projects.",
        category: 'projects',
        popular: true,
        link: "/projects"
    },
    {
        id: 11,
        question: "What information is required for project submission?",
        answer: "Required information includes: project title, description, core investigator, team members, objectives, activities, proposed budget, expected outcomes, evaluation plan, and sustainability plan. Start and end dates are also required.",
        category: 'projects',
        popular: false
    },
    {
        id: 12,
        question: "How do I edit my existing project?",
        answer: "When viewing your projects in 'My Projects' mode, you'll see Edit buttons on each project card. Click Edit to modify project details. All changes are tracked and saved to your profile.",
        category: 'projects',
        popular: false
    },
    {
        id: 13,
        question: "Can I delete a project?",
        answer: "Yes, project owners can delete their projects. Navigate to your projects list, find the project, and click the Delete button. Confirm deletion in the dialog box. This action cannot be undone.",
        category: 'projects',
        popular: false
    },
    {
        id: 14,
        question: "How are project statuses determined?",
        answer: "Project status is automatically calculated based on current date vs. project start and end dates. 'Upcoming' for future projects, 'Active' for ongoing projects, and 'Completed' for finished projects.",
        category: 'projects',
        popular: false
    },

    // Products Related FAQs
    {
        id: 15,
        question: "How do I submit a research product?",
        answer: "Go to Products section and click 'Submit'. Provide product title, description, details, composition, manufacturer, trade mark, and patent information. Products can include software, tools, methodologies, or physical products.",
        category: 'products',
        popular: true,
        link: "/product-submit"
    },
    {
        id: 16,
        question: "What product information is required?",
        answer: "Required fields include: product title, description, and details. Optional fields: composition, manufacturer, trade mark, and patent information for comprehensive product documentation.",
        category: 'products',
        popular: false
    },
    {
        id: 17,
        question: "How do I search for products?",
        answer: "Use the Products page search bar to search by title, description, manufacturer, composition, or trademark. You can also sort by newest first, oldest first, or alphabetically.",
        category: 'products',
        popular: false,
        link: "/products"
    },
    {
        id: 18,
        question: "What are product details?",
        answer: "Product details are specific features, specifications, or characteristics of your research product. You can add multiple detail entries to fully describe your product's capabilities.",
        category: 'products',
        popular: false
    },
    {
        id: 19,
        question: "Can I edit my products after submission?",
        answer: "Yes, product owners can edit their products. Navigate to 'My Products' mode by viewing your profile, find the product, and click Edit. Update any field including technical specifications.",
        category: 'products',
        popular: false
    },

    // Publications Related FAQs
    {
        id: 20,
        question: "How do I submit a publication?",
        answer: "Navigate to Publications section and click 'Submit'. Provide publication title, authors, abstract, keywords, journal/conference information, publication date, and DOI if available.",
        category: 'publications',
        popular: true,
        link: "/submit-publications"
    },
    {
        id: 21,
        question: "Where can I view all publications?",
        answer: "Go to the 'Publications' section in the main navigation to browse all published research papers, articles, and conference proceedings. Search by title, author, or keywords.",
        category: 'publications',
        popular: true,
        link: "/publications"
    },
    {
        id: 22,
        question: "What is 'My Publications'?",
        answer: "My Publications shows only your own submitted publications. Access this from the Publications dropdown menu to manage and view your academic publications.",
        category: 'publications',
        popular: false,
        link: "/my-publications"
    },
    {
        id: 23,
        question: "How do I cite a publication from the platform?",
        answer: "Each publication page includes a citation button that generates citations in multiple formats (APA, MLA, Chicago). Click the citation icon to copy the formatted citation.",
        category: 'publications',
        popular: false
    },

    // Account Related FAQs
    {
        id: 24,
        question: "How do I manage my profile?",
        answer: "Click on your name in the navigation and select 'Profile'. Here you can update personal information, research interests, affiliations, and notification preferences.",
        category: 'account',
        popular: true,
        link: "/profile"
    },
    {
        id: 25,
        question: "How do I reset my password?",
        answer: "Click 'Forgot Password' on the login page. Enter your registered email address and follow the reset link sent to your inbox. Choose a strong new password.",
        category: 'account',
        popular: true,
        link: "/forget-password"
    },
    {
        id: 26,
        question: "How do I provide feedback?",
        answer: "Navigate to the 'Feedback' section under More in the navigation. Submit suggestions, report issues, or share your experience to help improve the platform.",
        category: 'account',
        popular: false,
        link: "/feedback"
    },
    {
        id: 27,
        question: "How can I leave a testimonial?",
        answer: "Go to 'Testimonials' under the More section. Share your experience using the platform to help other researchers discover the value of our community.",
        category: 'account',
        popular: false,
        link: "/testimonials"
    },
    {
        id: 28,
        question: "How do I contact support?",
        answer: "Use the 'Contact Us' page under More. Fill out the contact form with your inquiry, or email support directly for technical assistance.",
        category: 'account',
        popular: false,
        link: "/contact-us"
    }
];

// Update category counts
faqCategories.forEach(category => {
    if (category.key !== 'all') {
        category.count = faqData.filter(faq => faq.category === category.key).length;
    }
});
faqCategories[0].count = faqData.length;

// Filter FAQs based on search and category
const filteredFAQs = faqData.filter(faq => {
    const matchesSearch = faq.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
                        faq.answer.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = activeCategory === 'all' || faq.category === activeCategory;
    return matchesSearch && matchesCategory;
});

const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: {
            staggerChildren: 0.1
        }
    }
};

const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
        y: 0,
        opacity: 1,
        transition: {
            duration: 0.5
        }
    }
};

const handleFAQChange = (panel) => (event, isExpanded) => {
    setExpandedFAQ(isExpanded ? panel : false);
};

const handleNavigate = (path) => {
    window.location.href = path;
};

return (
    <Box  sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
        <NavigationMenu />
        <Container Container maxWidth="lg" sx={{ py: 3, flex: 1, marginTop: 8 }}>
            {/* Header Section */}
            <Slide direction="down" in={true} mountOnEnter unmountOnExit>
                <Box sx={{
                    mb: 3,
                    p: 3,
                    borderRadius: 3,
                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                    color: 'white',
                    display: 'flex',
                    flexDirection: isMobile ? 'column' : 'row',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 2
                }}>
                    <Box>
                        <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold', mb: 1 }}>
                            Knowledge Centre
                        </Typography>
                        <Typography variant="subtitle1" sx={{ opacity: 0.9 }}>
                            Everything you need to make the most of the Research and Innovations Academy platform
                        </Typography>
                    </Box>
                </Box>
            </Slide>

            {/* YouTube Channel Section */}
            <motion.div
                variants={containerVariants}
                initial="hidden"
                animate="visible"
            >
                <Card sx={{ mb: 4, borderRadius: 3, overflow: 'hidden' }}>
                    <CardContent sx={{ p: 0 }}>
                        <Grid container>
                            <Grid item xs={12} md={4}>
                                <Box sx={{ 
                                    bgcolor: '#FF0000', 
                                    color: 'white', 
                                    p: 4, 
                                    height: '100%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    textAlign: 'center'
                                }}>
                                    <YouTubeIcon sx={{ fontSize: 64, mb: 2 }} />
                                    <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold' }}>
                                        {youtubeChannel.name}
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mb: 2 }}>
                                        <Chip 
                                            icon={<SubscriptionsIcon />} 
                                            label={`${youtubeChannel.subscribers} subscribers`}
                                            size="small"
                                            sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }}
                                        />
                                        <Chip 
                                            icon={<VideoIcon />} 
                                            label={`${youtubeChannel.totalVideos} videos`}
                                            size="small"
                                            sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }}
                                        />
                                    </Box>
                                    <Typography variant="body2" sx={{ mb: 2 }}>
                                        {youtubeChannel.description}
                                    </Typography>
                                    <Button 
                                        variant="contained" 
                                        startIcon={<YouTubeIcon />}
                                        sx={{ 
                                            bgcolor: '#fff', 
                                            color: '#FF0000',
                                            '&:hover': { bgcolor: '#fff', transform: 'translateY(-2px)' }
                                        }}
                                        onClick={() => window.open(youtubeChannel.url, '_blank')}
                                    >
                                        Subscribe to Channel
                                    </Button>
                                </Box>
                            </Grid>
                            <Grid item xs={12} md={8}>
                                <Box sx={{ position: 'relative', paddingTop: '56.25%' }}>
                                    <Box
                                        component="iframe"
                                        src="https://www.youtube.com/embed/videoseries?list=UUxxxxxxxxxxxxx"
                                        title="RIA Uganda YouTube Channel"
                                        sx={{
                                            position: 'absolute',
                                            top: 0,
                                            left: 0,
                                            width: '100%',
                                            height: '100%',
                                            border: 'none'
                                        }}
                                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                        allowFullScreen
                                    />
                                </Box>
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>
            </motion.div>

            {/* Platform Navigation Guide */}
            <motion.div variants={containerVariants} initial="hidden" animate="visible">
                <Paper elevation={3} sx={{ p: 4, borderRadius: 3, mb: 6 }}>
                    <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
                        <DashboardIcon color="primary" />
                        Platform Navigation Guide
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        Quick access to all platform sections and features
                    </Typography>
                    
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                            <Card variant="outlined" sx={{ p: 2 }}>
                                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                                    Research Module
                                </Typography>
                                <List dense>
                                    {ResearchLinks.map((link, index) => (
                                        <ListItem key={index} button onClick={() => handleNavigate(link.path)}>
                                            <ListItemIcon>
                                                <ScienceIcon color="primary" fontSize="small" />
                                            </ListItemIcon>
                                            <ListItemText primary={link.name} />
                                        </ListItem>
                                    ))}
                                </List>
                            </Card>
                        </Grid>
                        
                        <Grid item xs={12} md={6}>
                            <Card variant="outlined" sx={{ p: 2 }}>
                                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                                    Projects Module
                                </Typography>
                                <List dense>
                                    {ProjectsLinks.map((link, index) => (
                                        <ListItem key={index} button onClick={() => handleNavigate(link.path)}>
                                            <ListItemIcon>
                                                <TrendingUpIcon color="primary" fontSize="small" />
                                            </ListItemIcon>
                                            <ListItemText primary={link.name} />
                                        </ListItem>
                                    ))}
                                </List>
                            </Card>
                        </Grid>
                        
                        <Grid item xs={12} md={6}>
                            <Card variant="outlined" sx={{ p: 2 }}>
                                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                                    Products Module
                                </Typography>
                                <List dense>
                                    {ProductsLinks.map((link, index) => (
                                        <ListItem key={index} button onClick={() => handleNavigate(link.path)}>
                                            <ListItemIcon>
                                                <InventoryIcon color="primary" fontSize="small" />
                                            </ListItemIcon>
                                            <ListItemText primary={link.name} />
                                        </ListItem>
                                    ))}
                                </List>
                            </Card>
                        </Grid>
                        
                        <Grid item xs={12} md={6}>
                            <Card variant="outlined" sx={{ p: 2 }}>
                                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                                    Publications & More
                                </Typography>
                                <List dense>
                                    {PublicationLinks.map((link, index) => (
                                        <ListItem key={index} button onClick={() => handleNavigate(link.path)}>
                                            <ListItemIcon>
                                                <ArticleIcon color="primary" fontSize="small" />
                                            </ListItemIcon>
                                            <ListItemText primary={link.name} />
                                        </ListItem>
                                    ))}
                                    {MoreLinks.map((link, index) => (
                                        <ListItem key={index} button onClick={() => handleNavigate(link.path)}>
                                            <ListItemIcon>
                                                <MoreHorizIcon color="primary" fontSize="small" />
                                            </ListItemIcon>
                                            <ListItemText primary={link.name} />
                                        </ListItem>
                                    ))}
                                </List>
                            </Card>
                        </Grid>
                    </Grid>
                </Paper>
            </motion.div>

            <Grid container spacing={6}>
                {/* FAQs Section */}
                <Grid item xs={12} lg={12}>
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                    >
                        <Paper elevation={3} sx={{ p: 4, borderRadius: 4 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
                                <HelpIcon color="primary" sx={{ mr: 2, fontSize: 32 }} />
                                <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                                    Frequently Asked Questions
                                </Typography>
                            </Box>

                            {/* Search and Filter */}
                            <Box sx={{ mb: 3 }}>
                                <Paper
                                    sx={{
                                        p: '2px 4px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        mb: 2
                                    }}
                                >
                                    <SearchIcon sx={{ color: 'text.secondary', mx: 1 }} />
                                    <input
                                        placeholder="Search FAQs by question, answer, or category..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        style={{
                                            border: 'none',
                                            outline: 'none',
                                            flex: 1,
                                            padding: '8px',
                                            fontSize: '16px',
                                            background: 'transparent'
                                        }}
                                    />
                                </Paper>
                                
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                    {faqCategories.map((category) => (
                                        <Chip
                                            key={category.key}
                                            icon={category.icon}
                                            label={`${category.label} (${category.count})`}
                                            onClick={() => setActiveCategory(category.key)}
                                            color={activeCategory === category.key ? 'primary' : 'default'}
                                            variant={activeCategory === category.key ? 'filled' : 'outlined'}
                                            size="medium"
                                        />
                                    ))}
                                </Box>
                            </Box>

                            {/* FAQs List */}
                            <Box>
                                <AnimatePresence>
                                    {filteredFAQs.map((faq, index) => (
                                        <motion.div
                                            key={faq.id}
                                            initial={{ opacity: 0, height: 0 }}
                                            animate={{ opacity: 1, height: 'auto' }}
                                            exit={{ opacity: 0, height: 0 }}
                                            transition={{ duration: 0.3 }}
                                        >
                                            <Accordion
                                                expanded={expandedFAQ === `panel${faq.id}`}
                                                onChange={handleFAQChange(`panel${faq.id}`)}
                                                sx={{
                                                    mb: 2,
                                                    borderRadius: '8px !important',
                                                    '&:before': { display: 'none' }
                                                }}
                                            >
                                                <AccordionSummary
                                                    expandIcon={<ExpandMoreIcon />}
                                                    sx={{
                                                        borderRadius: '8px',
                                                        bgcolor: expandedFAQ === `panel${faq.id}` ? 'primary.light' : 'transparent',
                                                        '&:hover': {
                                                            bgcolor: alpha(theme.palette.primary.main, 0.04)
                                                        }
                                                    }}
                                                >
                                                    <Box sx={{ display: 'flex', alignItems: 'flex-start', width: '100%' }}>
                                                        <HelpIcon 
                                                            sx={{ 
                                                                mr: 2, 
                                                                color: 'primary.main',
                                                                mt: 0.5 
                                                            }} 
                                                        />
                                                        <Box sx={{ flex: 1 }}>
                                                            <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600 }}>
                                                                {faq.question}
                                                            </Typography>
                                                            <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                                                                <Chip 
                                                                    label={faqCategories.find(c => c.key === faq.category)?.label} 
                                                                    size="small" 
                                                                    variant="outlined"
                                                                />
                                                                {faq.popular && (
                                                                    <Chip 
                                                                        label="Popular" 
                                                                        size="small" 
                                                                        color="primary" 
                                                                    />
                                                                )}
                                                            </Box>
                                                        </Box>
                                                    </Box>
                                                </AccordionSummary>
                                                <AccordionDetails sx={{ bgcolor: 'grey.50', borderBottomLeftRadius: '8px', borderBottomRightRadius: '8px' }}>
                                                    <Typography variant="body1" sx={{ lineHeight: 1.6, mb: 2 }}>
                                                        {faq.answer}
                                                    </Typography>
                                                    {faq.link && (
                                                        <Button 
                                                            variant="outlined" 
                                                            size="small"
                                                            onClick={() => handleNavigate(faq.link)}
                                                            sx={{ mt: 1 }}
                                                        >
                                                            Go to {faq.link.split('/')[1]}
                                                        </Button>
                                                    )}
                                                </AccordionDetails>
                                            </Accordion>
                                        </motion.div>
                                    ))}
                                </AnimatePresence>

                                {filteredFAQs.length === 0 && (
                                    <Box textAlign="center" sx={{ py: 4 }}>
                                        <HelpIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                                        <Typography variant="h6" color="text.secondary">
                                            No FAQs found
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Try adjusting your search or filter criteria
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                        </Paper>
                    </motion.div>
                </Grid>
            </Grid>

            {/* Floating Action Button */}
            <Fab
                color="primary"
                aria-label="help"
                sx={{
                    position: 'fixed',
                    bottom: 24,
                    right: 24,
                }}
                onClick={() => window.open('mailto:support@riauganda.org', '_blank')}
            >
                <HelpIcon />
            </Fab>
        </Container>
    </Box>
);
};

export default GeneralResources;